import type { QueryClient } from "@tanstack/react-query"
import type {
	AnyRouter,
	NavigateOptions,
	RegisteredRouter,
} from "@tanstack/react-router"
import posthog from "posthog-js"
import { registerSessionCleanup } from "@/shared/lib/auth"
import { removeSystemNotification } from "@/shared/lib/platform"
import type {
	NotificationResponseInputBody,
	WsNotification,
} from "../generated-social"
import { zNotificationResponseInputBody } from "../generated-social/zod.gen"
import { waitForConnection } from "./store"

export interface NotificationHandlerContext {
	queryClient: QueryClient
	router: AnyRouter
	userId: number
}

// A navigation target stored per notification: pinned to the root route, its
// path and params checked against the route tree.
type NotificationRoute<TTo extends string | undefined = string> =
	NavigateOptions<RegisteredRouter, "/", TTo>

export type NotificationName = WsNotification["name"]

export type NotificationResponseName = NonNullable<
	NotificationResponseInputBody["name"]
>

type NotificationOf<TName extends NotificationName> = Extract<
	WsNotification,
	{ name: TName }
>

// What a slice registers for its notification: what to do with it, the screen
// it points at, and the popup opened over that screen after a push tap.
interface NotificationHandlerConfig<
	TName extends NotificationName,
	TTo extends string | undefined,
> {
	handle: (
		notification: NotificationOf<TName>,
		ctx: NotificationHandlerContext,
	) => void
	route: (notification: NotificationOf<TName>) => NotificationRoute<TTo>
	routePopup: ((notification: NotificationOf<TName>) => void) | null
}

interface RegisteredNotification
	extends NotificationHandlerConfig<NotificationName, string> {
	name: NotificationName
}

interface HandledNotification {
	responseName: NotificationResponseName | null
	route: NotificationRoute
	routePopup: (() => void) | null
}

export type TapSource = "push" | "telegram"

interface OpenedNotification extends HandledNotification {
	notificationId: number
	source: TapSource
}

const registry = new Map<string, RegisteredNotification>()
const handledNotifications = new Map<number, HandledNotification>()
const openTargets = new Map<number, HandledNotification>()
const waiters = new Map<
	number,
	(notification: HandledNotification | null) => void
>()
const tapped = new Map<number, TapSource>()

export function registerNotification<
	TName extends NotificationName,
	TTo extends string | undefined,
>(name: TName, config: NotificationHandlerConfig<TName, TTo>): void {
	const erased = config as unknown as NotificationHandlerConfig<
		NotificationName,
		string
	>
	registry.set(name, { name, ...erased })
}

export function handleNotification(
	notification: WsNotification,
	ctx: NotificationHandlerContext,
): void {
	const { name, notification_id: notificationId } = notification
	const config = registry.get(name)
	if (!config) {
		console.warn(`[Notification] No handler for "${name}"`)
		posthog.capture("ws_unknown_notification", { name })
		return
	}

	// Delivery repeats until the notification is confirmed: an id already seen in
	// this session is dropped.
	if (handledNotifications.has(notificationId)) return

	// Marked before the handler runs, so a handler that cannot act may release
	// the id synchronously.
	const handled: HandledNotification = {
		responseName: responseNameOf(config.name),
		route: config.route(notification),
		routePopup: bindRoutePopup(config, notification),
	}
	handledNotifications.set(notificationId, handled)
	openTargets.set(notificationId, handled)

	config.handle(notification, ctx)

	waiters.get(notificationId)?.(handled)
}

// Null when the response contract carries no such name: an answer then goes
// without one.
function responseNameOf(
	name: NotificationName,
): NotificationResponseName | null {
	const parsed = zNotificationResponseInputBody.shape.name
		.unwrap()
		.safeParse(name)
	if (!parsed.success) return null
	return parsed.data
}

function bindRoutePopup(
	config: RegisteredNotification,
	notification: WsNotification,
): (() => void) | null {
	const { routePopup } = config
	if (routePopup === null) return null
	return () => routePopup(notification)
}

/**
 * Frees an id the handler could not act on — its popup was never shown — so the
 * server's replay is accepted instead of dropped as already handled.
 * `openTargets` / `tapped` are left alone: a tap's navigation is closed by
 * `settleNotification`.
 */
export function releaseNotificationId(notificationId: number): void {
	handledNotifications.delete(notificationId)
}

export function markTapped(notificationId: number, source: TapSource): void {
	tapped.set(notificationId, source)
}

export function settleNotification(notificationId: number): void {
	openTargets.delete(notificationId)
	tapped.delete(notificationId)
	// The notification is answered, so its push has nothing left to say.
	void removeSystemNotification(notificationId)
}

function resetNotificationRegistry(): void {
	handledNotifications.clear()
	openTargets.clear()
	for (const resolve of waiters.values()) resolve(null)
	waiters.clear()
	tapped.clear()
}

/**
 * The notification behind an id, once it arrives over the websocket. Null when
 * the socket stays shut for `connectMs`, or when nothing arrives within
 * `burstMs` of it opening.
 */
export async function waitForNotification(
	notificationId: number,
	{ connectMs, burstMs }: { connectMs: number; burstMs: number },
): Promise<HandledNotification | null> {
	const handled = handledNotifications.get(notificationId)
	if (handled) return handled

	let resolveArrival: (notification: HandledNotification | null) => void =
		() => {}
	const arrival = new Promise<HandledNotification | null>((resolve) => {
		resolveArrival = resolve
		waiters.set(notificationId, resolve)
	})
	const deadline = waitForConnection(connectMs).then(async (connected) => {
		if (!connected) return
		await new Promise<void>((resolve) => setTimeout(resolve, burstMs))
	})

	try {
		return await Promise.race([arrival, deadline.then(() => null)])
	} finally {
		if (waiters.get(notificationId) === resolveArrival)
			waiters.delete(notificationId)
	}
}

/**
 * Notifications the user reached by tapping their push or Telegram button,
 * removed from the registry. Only the tap counts: walking onto the same screen
 * unprompted says nothing about the notification, so it is left alone.
 */
export function takeOpenedNotifications(
	isOpen: (route: NotificationRoute) => boolean,
): OpenedNotification[] {
	const opened: OpenedNotification[] = []
	for (const [notificationId, target] of openTargets) {
		const source = tapped.get(notificationId)
		if (source === undefined) continue
		if (!isOpen(target.route)) continue

		settleNotification(notificationId)
		opened.push({ notificationId, source, ...target })
	}
	return opened
}

registerSessionCleanup(resetNotificationRegistry)
